import { ImageType, getWebPath, isImageTypeDocument, isPreviewableType } from '@doss/common-all'
import { Box } from '@mui/material'
import FileSaver from 'file-saver'
import { truncate } from 'lodash'
import { X } from 'lucide-react'
import { MouseEventHandler, useEffect, useMemo, useState } from 'react'
import { BsFiletypeAi, BsFiletypeHtml, BsFiletypePdf } from 'react-icons/bs'
import {
  PiFile,
  PiFileCsv,
  PiFileText,
  PiFileZip,
  PiMicrosoftExcelLogo,
  PiMicrosoftPowerpointLogo,
  PiMicrosoftWordLogo,
} from 'react-icons/pi'
import { tss } from 'tss-react/mui'
import { BreadcrumbObjectType, DocumentType } from '~/common/generated/apollo-operations'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { uiColors } from '~/common/styles/colors'
import { FileCellDocument } from '~/components/records/record-cell-details/types'
import { Tooltip } from '~/components/ui/atoms/tooltip/Tooltip'

interface FileInlineImageProps {
  document: FileCellDocument
  onDelete?: MouseEventHandler<HTMLButtonElement> | undefined
  size?: number
  disabled?: boolean
}

interface DocumentIconProps {
  documentType: Exclude<DocumentType, ImageType>
  className: string
  size?: number
}

/**
 * Renders the icon for non-image document types. The default size is based on the
 * size of the MUI Data Grid Row when the density is comfortable
 */
function DocumentIcon({ documentType, className, size = 63 }: DocumentIconProps) {
  switch (documentType) {
    case DocumentType.AI:
      return <BsFiletypeAi className={className} size={size} />
    case DocumentType.DWG:
    case DocumentType.CSV:
    case DocumentType.SIF:
      return <PiFileCsv className={className} size={size} />
    case DocumentType.DOC:
    case DocumentType.DOCX:
      return <PiMicrosoftWordLogo className={className} size={size} />
    case DocumentType.HTML:
      return <BsFiletypeHtml className={className} size={size} />
    case DocumentType.PDF:
      return <BsFiletypePdf className={className} size={size} />
    case DocumentType.PPT:
    case DocumentType.PPTX:
      return <PiMicrosoftPowerpointLogo className={className} size={size} />
    case DocumentType.STEP:
    case DocumentType.THREEDM:
      return <PiFile className={className} size={size} />
    case DocumentType.TXT:
    case DocumentType.ZPL:
      return <PiFileText className={className} size={size} />
    case DocumentType.XLS:
    case DocumentType.XLSM:
    case DocumentType.XLSX:
      return <PiMicrosoftExcelLogo className={className} size={size} />
    case DocumentType.ZIP:
      return <PiFileZip className={className} size={size} />
  }
}

/**
 * Renders the image for image files or an icon for other types. The default size is based on the
 * size of the MUI Data Grid Row when the density is comfortable
 */
export function FileInlineImage({ document, onDelete, size = 60 }: FileInlineImageProps) {
  const documentType = document.documentType
  const isImage = isImageTypeDocument(documentType)
  const { classes } = useStyles({ size })
  const { user } = useGlobalContext()
  const isUnauth = !user
  const [imageContent, setImageContent] = useState('')

  useEffect(() => {
    if (!isImage) {
      setImageContent('')
      return
    }

    // Blob URLs (created by FileChipsDisplay for local files) can be used directly
    // Regular storage URLs should be fetched to get SVG content
    const isBlobUrl = document.storageUrl.startsWith('blob:')

    if (isBlobUrl) {
      // Use blob URL directly as image source
      setImageContent(document.storageUrl)
      // Clear state when component unmounts or document changes
      return () => {
        setImageContent('')
      }
    } else {
      // Fetch from storage URL (for SVG files that need XML parsing)
      let cancelled = false

      fetch(document.storageUrl)
        .then((response) => response.text())
        .then((text) => {
          if (!cancelled) {
            setImageContent(text)
          }
        })
        .catch((err) => {
          if (!cancelled) {
            console.error('Failed to fetch image from storage:', err)
          }
        })

      return () => {
        cancelled = true
        setImageContent('')
      }
    }
  }, [document, isImage])

  const renderContent = () => {
    if (isImage) {
      if (imageContent.startsWith('<?xml')) {
        return (
          <Box
            className={classes.image}
            component="div"
            dangerouslySetInnerHTML={{ __html: imageContent }}
          />
        )
      } else {
        return (
          <Box
            alt={document.name}
            className={classes.image}
            component="img"
            crossOrigin="anonymous"
            src={document.storageUrl}
          />
        )
      }
    } else {
      return <DocumentIcon className={classes.icon} documentType={documentType} size={size} />
    }
  }

  const fileNameDisplay = useMemo(() => {
    const [name, extension] = document.name.split('.')

    return truncate(name, { length: 50, omission: '...' }) + '.' + extension
  }, [document.name])

  const handleClick = async () => {
    if (!isPreviewableType(document.documentType)) {
      const response = await fetch(document.storageUrl)
      const blob = await response.blob()
      FileSaver.saveAs(blob, fileNameDisplay)
    } else {
      const fileUrl = getWebPath({
        objectId: document.id,
        objectType: BreadcrumbObjectType.DOCUMENT,
        readOnly: isUnauth,
      })
      window.open(fileUrl, '_blank')
    }
  }

  return (
    <div
      className={`max-h-[ relative inline-block min-w-[50px]${size}px] group box-border flex w-fit items-center justify-center`}
    >
      <Tooltip arrow title={document.name}>
        <div
          className={`h-[ flex w-auto max-w-full items-start justify-start overflow-hidden${size}px] relative min-h-full min-w-full rounded-[2px] border border-transparent hover:border-doss-active`}
          onClick={handleClick}
        >
          {renderContent()}
        </div>
      </Tooltip>
      {onDelete && (
        <button
          className={`absolute end-0 top-0 hidden rounded-[2px] bg-doss-background p-0.5 group-hover:!flex`}
          onClick={onDelete}
        >
          <X size={12} />
        </button>
      )}
    </div>
  )
}

const useStyles = tss
  .withParams<{
    size: number
  }>()
  .create(({ size }) => {
    return {
      image: {
        width: '100%',
        height: '100%',
        display: 'block',
        objectFit: 'contain',
        padding: '4px',
        boxSizing: 'border-box',
        maxHeight: size,
      },
      icon: {
        width: '100%',
        height: '100%',
        display: 'block',
        objectFit: 'contain',
        padding: '10px',
        boxSizing: 'border-box',
        maxHeight: size,
      },

      link: {
        textDecoration: 'none',
        color: uiColors.content.disabled,
        width: 'fit-content',
        display: 'inline-block',
      },
    }
  })
